import { useEffect, useState } from "react";
import api from "../../services/api";
import { Col, Row } from "react-bootstrap";
import useWrapper from "../../hooks/useWrapper";
import usePermission from "../../hooks/usePermission";
import { toast } from "react-toastify";
import { formatStatus } from "../../hooks/api/listAttendance";
import useFormat from "../../hooks/utils/useFormat";
import StatusFlag from "../StatusFlag";
import style from "./style.module.scss";
import Avatar from "../Avatar";
import { InputTextArea } from "../Input";
import Button from "../Button";
import IcFile from "../Svg/IcFile";
import { ModalFilePreview, ModalConfirm, ModalEndService, ModalPrivateComment, ModalChangeAttendant } from "../Modal";
import Loading from "../Loading";
import FileUpload from "../FileUpload";
import { uploadFile } from "../../hooks/api/uploadFile";
import connection from "../../services/ws";
import ButtonActions from "../Button/ButtonActions";
import StarRate from "../StarRate";
import { PrintPage } from "../PrintPage";
import printRules from "../PrintRules/style.module.scss";

function FileIco(props) {
  const { file, ...rest } = props;
  return (
    <div className={style.file} {...rest}>
      <IcFile />
      <p className="m-0">{file?.name}</p>
      <p className="m-0 small-label">{file?.size || ""}</p>
    </div>
  );
}

export function MessageBaloon(props) {
  const { message, name, sender_is_self, is_private, date, hide_avatar, files, downloadFile } = props;
  return (
    <div
      className={`${style.container} ${!!is_private ? printRules.hide_on_print : ""}`}
      data-sender-self={sender_is_self}
      data-is-private={is_private}
    >
      <span className={style.avatar} data-hide={hide_avatar}>
        <Avatar username={name || ""} />
      </span>
      <div className={style.baloon}>
        <p className={style.sender_name}>{name}</p>
        {is_private && <p className={style.private_label}>(Comentário Privado)</p>}
        {files?.length > 0 && (
          <div className={style.files_container}>
            {files?.map(__file => (
              <FileIco
                file={__file}
                onClick={() => {
                  downloadFile(__file);
                }}
              />
            ))}
          </div>
        )}
        <span className={style.message_text}>{message}</span>
        <p className={style.date}>{date}</p>
      </div>
    </div>
  );
}

export default function AttendanceChat(props) {
  const { id: attendance_id } = props;
  const { config, me, token } = useWrapper();
  const { formatPhone, formatDate, formatFileSize } = useFormat();
  const prefix = me?.type === "Formando" ? "trainee/" : "";
  const isGestor = me?.type === "Gestor";
  const permSendMessages = usePermission("attendance", "sendMessages");
  const permUpdateInfos = usePermission("attendance", "updateInfos");
  const permFinish = usePermission("attendance", "finishAttendance");
  const permFileReading = usePermission("attendance", "fileReading");
  const permFileCreate = usePermission("attendance", "fileCreate");
  // Formandos are not subject to the staff permission grid — gates below only apply to Gestor users.
  const canSendMessages = !isGestor || permSendMessages;
  const canUpdateInfos = !isGestor || permUpdateInfos;
  const canFinish = !isGestor || permFinish;
  const canFileReading = !isGestor || permFileReading;
  const canFileCreate = !isGestor || permFileCreate;

  const [attendance, setattendance] = useState();
  const [messages, setmessages] = useState();
  const [modal, setmodal] = useState();

  const [isLoadingSave] = useState(false);
  const [isLoadingMessages, setisLoadingMessages] = useState(true);
  const [isWaitingResponse, setisWaitingResponse] = useState(true);

  useEffect(() => {
    setisWaitingResponse(isLoadingSave || isLoadingMessages);
  }, [isLoadingSave, isLoadingMessages]);

  useEffect(() => {
    (async () => {
      try {
        setisLoadingMessages(true);
        const { data: _attendance } = await api.get(`${prefix}attendance/${attendance_id}`, config);
        const { data: _messages } = await api.get(
          `${prefix}attendance-messages?attendance_id=${attendance_id}`,
          config,
        );
        setattendance({
          ..._attendance,
          status: formatStatus({
            status: _attendance?.status,
            is_urgent: _attendance?.status_urgent && me?.type === "Gestor",
          }),
        });
        setmessages(
          _messages
            ?.filter(__message => __message?.files?.length > 0 || !!__message?.message)
            ?.filter(__message => !__message?.private || me?.type === "Gestor")
            ?.map(__message => formatMessage(__message)),
        );
      } catch (__error) {
        console.log("__error", __error);
        toast.error("Erro ao tentar carregar dados do atendimento.");
      }
    })();
    // Runs once on mount to load the attendance/messages for this component instance;
    // attendance_id/config/prefix/me?.type are stable for the component's lifetime and
    // formatMessage is redefined every render, so including them would just re-trigger the fetch.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const formatMessage = __message => {
    return {
      ...__message,
      files: __message?.files?.map(__file => ({
        ...__file,
        id: __file.id,
        name: __file.name,
        size: formatFileSize(__file?.size || 0),
        download: __file.download,
      })),
    };
  };

  const [isConnected, setisConnected] = useState(false);
  useEffect(() => {
    if (!isConnected && messages) {
      try {
        connection.connect(token);
        connection.subscribe(
          me?.type === "Gestor" ? `attendance:${attendance_id}` : `attendance-trainee:${attendance_id}`,
          handleWS,
        );
      } catch (__error) {
        console.log("__error", __error);
        toast.error("Erro ao tentar se conectar ao atendimento.");
      } finally {
        setisConnected(true);
        setisLoadingMessages(false);
        // return () => {
        //   connection.disconnect();
        // };
      }
    }
    // WS subscribe guarded by isConnected so it only runs once messages are first loaded;
    // handleWS/attendance_id/me?.type/token are stable for the component's lifetime and adding
    // isConnected/handleWS here risks resubscribing on every incoming message.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [messages]);

  const [newMessage, setnewMessage] = useState();

  const submitMessage = async () => {
    if (!canSendMessages) return false;
    if (!newMessage?.text && !(newMessage?.files?.length > 0)) return false;
    try {
      setisWaitingResponse(true);
      let _files = [];
      if (newMessage?.files?.length > 0) {
        try {
          for (const __file of newMessage?.files) {
            const { data: _file } = await uploadFile({
              file: __file,
              token,
              prefix,
            });
            _files.push(_file.id);
          }
        } catch (__error) {
          console.log("__error", __error);
          toast.error("Erro ao tentar subir os arquivos.");
        }
      }
      await api.post(
        `${prefix}attendance-messages`,
        {
          message: newMessage?.text || "",
          private: false,
          attendance_id: attendance_id,
          files: _files,
          ...(me?.type === "Formando"
            ? {
                trainee_id: me?.id,
              }
            : {
                user_id: me?.id,
              }),
        },
        config,
      );
      me?.type === "Gestor" && (await api.put(`attendance/${attendance_id}`, { read: 1 }, config));
    } catch (__error) {
      console.log("__error", __error);
      toast.error("Erro ao tentar enviar mensagem.");
    } finally {
      setisWaitingResponse(false);
      setnewMessage(null);
    }
  };

  function handleWS(__message) {
    const { type, data } = __message;
    switch (type) {
      case "socket-attendance:newMessage":
        if (!data?.private || me?.type === "Gestor") {
          setmessages(prevState => [formatMessage(data), ...prevState]);
        }
        break;
      case "socket-attendance:changeInfo":
        setattendance({
          ...attendance,
          ...data,
          status: formatStatus({
            status: data?.status,
            is_urgent: data?.status_urgent && me?.type === "Gestor",
          }),
        });
        break;
      default:
        break;
    }
  }

  async function finishAttendance() {
    if (!canFinish) return;
    try {
      setisWaitingResponse(true);
      await api.put(
        `${prefix}attendance/${attendance_id}`,
        {
          status: "resolvido",
          user_attended: attendance?.user?.id,
        },
        config,
      );
      toast.success("Atendimento finalizado com sucesso!");
    } catch (__error) {
      console.log("__error", __error);
      toast.error("Erro ao tentar finalizar o atendimento.");
    } finally {
      setisWaitingResponse(false);
    }
    setmodal();
  }

  async function reopenAttendance() {
    try {
      setisWaitingResponse(true);
      await api.put(
        `${prefix}attendance/${attendance_id}`,
        {
          status: "aguardandoFormando",
          user_attended: null,
        },
        config,
      );
      toast.success("Atendimento reaberto com sucesso!");
    } catch (__error) {
      console.log("__error", __error);
      toast.error("Erro ao tentar reabrir o atendimento.");
    } finally {
      setisWaitingResponse(false);
    }
    setmodal();
  }

  return (
    <>
      <>
        {!isLoadingMessages ? (
          <>
            {(() => {
              const _header = (
                <Row className="mt-2">
                  <Col>
                    <h2 class="mini-title">Atendimento {attendance_id}</h2>
                    <p class="small-label m-0">
                      <b>Data de Envio: </b> {formatDate(attendance?.created_at, 5)}
                    </p>
                    <p class="small-label m-0">
                      <b>Última Interação: </b> {formatDate(attendance?.modified_at, 5)}
                    </p>
                    <p class="small-label m-0">
                      <b>Categoria/Recurso: </b>{" "}
                      {`${attendance?.category?.name || ""} - ${attendance?.resource?.name || ""}`}
                    </p>
                    {!!attendance?.user?.username && (
                      <p class="small-label m-0">
                        <b>Atendente Responsável: </b> {`${attendance?.user?.username || "-"}`}
                      </p>
                    )}
                    <span className={printRules.hide_on_print}>
                      <StatusFlag status={attendance?.status} />
                    </span>
                  </Col>
                  {me?.type === "Gestor" && (
                    <Col lg={4}>
                      <h2 class="mini-title">
                        {!!attendance?.commission_only && "Comissão"}
                        {!!attendance?.commission_only && !!attendance?.trainee && " - "}
                        {attendance?.trainee?.name || ""}
                      </h2>
                      {!!attendance?.commercial && (
                        <p class="small-label m-0">
                          <b>{`${attendance?.commercial?.course?.title} ${attendance?.commercial?.institution?.title} ${
                            attendance?.commercial?.year
                          }.${attendance?.commercial?.period} - ${
                            attendance?.commercial?.contract_number_legacy || ""
                          }`}</b>
                        </p>
                      )}
                      {!!attendance?.trainee && (
                        <>
                          <p class="small-label mb-0">{attendance?.trainee?.email || ""}</p>
                          <p class="small-label mb-0">{formatPhone(attendance?.trainee?.cell) || ""}</p>
                        </>
                      )}
                    </Col>
                  )}
                  <Col lg={4} className={`text-end ${printRules.hide_on_print}`}>
                    {!attendance?.avaliation && (
                      <>
                        {me?.type === "Gestor" ? (
                          <ButtonActions
                            disabled={!!isWaitingResponse}
                            options={[
                              ...(canSendMessages
                                ? [
                                    {
                                      label: "Adicionar Comentário Privado",
                                      onClick: () => {
                                        setmodal({
                                          type: "private_comment",
                                        });
                                      },
                                    },
                                  ]
                                : []),
                              ...(canUpdateInfos
                                ? [
                                    {
                                      label: "Modificar Atendimento",
                                      onClick: () => {
                                        setmodal({
                                          type: "change_attendance",
                                        });
                                      },
                                    },
                                  ]
                                : []),
                              ...(canFinish
                                ? attendance?.status !== "Cancelado" && attendance?.status !== "Resolvido"
                                  ? [
                                      {
                                        label: "Finalizar Atendimento",
                                        onClick: () => {
                                          setmodal({
                                            type: "finish",
                                          });
                                        },
                                      },
                                    ]
                                  : [
                                      {
                                        label: "Reabrir o Atendimento",
                                        onClick: () => {
                                          setmodal({
                                            type: "reopen",
                                          });
                                        },
                                      },
                                    ]
                                : []),
                              {
                                label: "Imprimir Atendimento",
                                onClick: () => {
                                  window.print();
                                },
                              },
                            ]}
                          />
                        ) : (
                          <>
                            {attendance?.status !== "Cancelado" &&
                              attendance?.status !== "Resolvido" &&
                              me?.id === attendance?.trainee_id && (
                                <Button
                                  loading={!!isWaitingResponse}
                                  color={"error"}
                                  onClick={() => {
                                    setmodal({
                                      type: "finish",
                                    });
                                  }}
                                >
                                  Finalizar Atendimento
                                </Button>
                              )}
                          </>
                        )}
                      </>
                    )}
                  </Col>
                </Row>
              );
              return (
                <PrintPage has_logo={true} header={_header}>
                  <div className={printRules.hide_on_print}>{_header}</div>
                  <div className="divisor mt-3 mb-3"></div>
                  <div className={style.messages_container}>
                    {(() => {
                      let _last_sender = null;
                      return (
                        <>
                          {messages?.map(__message => {
                            const username = __message?.user?.username || __message?.trainee?.name || "";
                            let _hide_avatar = username === _last_sender;
                            _last_sender = username;
                            return (
                              <MessageBaloon
                                message={__message?.message}
                                sender_is_self={
                                  (!!__message?.trainee_id && me?.type === "Formando") ||
                                  (!__message?.trainee_id && me?.type === "Gestor")
                                }
                                name={username}
                                hide_avatar={_hide_avatar}
                                is_private={!!__message?.private}
                                date={formatDate(__message.created_at, 4)}
                                files={canFileReading ? __message?.files : []}
                                downloadFile={__file => {
                                  setmodal({
                                    type: "download_file",
                                    file: __file,
                                  });
                                }}
                              />
                            );
                          })}
                        </>
                      );
                    })()}
                  </div>
                  {attendance?.status !== "Resolvido" && attendance?.status !== "Cancelado" && (
                    <div className={printRules.hide_on_print}>
                      <>
                        {isGestor && !canSendMessages ? (
                          <>
                            <div className="divisor mt-3 mb-3"></div>
                            <p className="text-center m-0">
                              Você não tem permissão para enviar mensagens em atendimentos.
                            </p>
                          </>
                        ) : me?.id === attendance?.trainee_id ||
                          (!attendance?.trainee_id && !!attendance?.commission_only) ||
                          me?.type === "Gestor" ? (
                          <>
                            <InputTextArea
                              required={true}
                              disabled={!!isWaitingResponse}
                              value={newMessage?.text || ""}
                              onChange={__ev => {
                                setnewMessage({
                                  ...newMessage,
                                  text: __ev.target.value,
                                });
                              }}
                              placeholder="Escreva sua mensagem"
                            />
                            <div className="d-flex justify-content-between flex-grow-1 mt-2">
                              {canFileCreate ? (
                                <FileUpload
                                  files={newMessage?.files}
                                  setfiles={__ev => {
                                    setnewMessage({
                                      ...newMessage,
                                      files: __ev,
                                    });
                                  }}
                                  loading={!!isWaitingResponse}
                                />
                              ) : (
                                <span />
                              )}
                              <Button
                                className="ml-2"
                                loading={!!isWaitingResponse}
                                disabled={!newMessage?.text && !(newMessage?.files?.length > 0)}
                                type="submit"
                                onClick={() => {
                                  if (
                                    !attendance?.trainee_id &&
                                    !!attendance?.commission_only &&
                                    me?.type !== "Gestor"
                                  ) {
                                    setmodal({
                                      type: "comission_response",
                                    });
                                  } else {
                                    submitMessage();
                                  }
                                }}
                              >
                                Enviar
                              </Button>
                            </div>
                          </>
                        ) : (
                          <>
                            <div className="divisor mt-3 mb-3"></div>
                            <p className="text-center m-0">
                              Este atendimento já está sendo respondido pelo membro da comissão{" "}
                              <b>{attendance?.trainee?.name}</b>
                            </p>
                          </>
                        )}
                      </>
                    </div>
                  )}
                  {!attendance?.avaliation && attendance?.status === "Resolvido" && me?.type === "Formando" && (
                    <div className="text-center">
                      <div className="divisor mt-3 mb-3"></div>
                      <p className="mini-title">Esse Atendimento foi classificado com Resolvido</p>
                      {me?.id === attendance?.trainee_id ? (
                        <>
                          <p className="small-label">
                            Se você ainda tem alguma dúvida ou outra questão à ser resolvida, escolha a opção{" "}
                            <b>Reabrir Ticket</b>. Caso todas as suas questões tenham sido resolvidas por hora, por
                            favor, ecolha a opção <b>Concluir Atendimento</b> e reserve um minutinho para responder a
                            nossa pesquisa de satisfação.
                          </p>
                          <div>
                            <Button
                              loading={!!isWaitingResponse}
                              color={"primary"}
                              onClick={() => {
                                setmodal({
                                  type: "reopen",
                                });
                              }}
                            >
                              Reabrir Atendimento
                            </Button>
                            <Button
                              loading={!!isWaitingResponse}
                              color={"success"}
                              onClick={() => {
                                setmodal({
                                  type: "rate",
                                });
                              }}
                            >
                              Concluir Atendimento
                            </Button>
                          </div>
                        </>
                      ) : (
                        <>
                          <p className="small-label m-0">
                            Se a turma ainda tem alguma dúvida ou outra questão à ser resolvida, o formando{" "}
                            <b>{attendance?.trainee?.name}</b> poderá <b>Reabrir Ticket</b>.
                          </p>
                          <p className="small-label m-0">
                            Caso todas as suas questões tenham sido resolvidas por hora, ele também poderá{" "}
                            <b>Concluir Atendimento</b> e responder a nossa pesquisa de satisfação.
                          </p>
                        </>
                      )}
                    </div>
                  )}

                  {!attendance?.avaliation && attendance?.status === "Resolvido" && me?.type === "Gestor" && (
                    <div className="text-center">
                      <div className="divisor mt-3 mb-3"></div>
                      <p className="mini-title">Esse Atendimento foi classificado com Resolvido</p>
                      <p className="small-label m-0">
                        Se você ainda tem alguma questão à ser resolvida, escolha a opção <b>Reabrir Ticket</b>.
                      </p>
                      <p className="small-label m-0">
                        O formando poderá concluir e avaliar este atendimento quando for conveniente.
                      </p>
                    </div>
                  )}
                  {!!attendance?.avaliation && me?.type === "Formando" && (
                    <div className="text-center">
                      <div className="divisor mt-3 mb-3"></div>
                      <p className="mini-title">Esse Atendimento está concluído</p>
                      <p className="small-label m-0">Agradecemos a avaliação.</p>
                    </div>
                  )}
                  {!!attendance?.avaliation && me?.type === "Gestor" && (
                    <div className="text-center">
                      <div className="divisor mt-3 mb-3"></div>
                      <p className="mini-title mb-3">Esse Atendimento está concluído e foi avaliado</p>
                      <StarRate readonly rate={attendance?.avaliation} setRate={() => {}} className="mb-3" />
                      <p className="small-label m-0">
                        Segundo o formando atendido, ele {!attendance?.attended ? "não" : ""} teve a sua solicitação
                        resolvida.
                      </p>
                      <p className="m-0">"{attendance?.attended_text}"</p>
                    </div>
                  )}
                </PrintPage>
              );
            })()}
          </>
        ) : (
          <Loading />
        )}
      </>
      <ModalFilePreview
        view={modal?.type === "download_file"}
        file={modal?.file}
        closeModal={() => {
          setmodal();
        }}
        prefix={prefix}
      />
      <ModalConfirm
        show={modal?.type === "finish"}
        title="Finalizar Atendimento"
        text="Tem certeza que deseja finalizar este atendimento?"
        confirm={finishAttendance}
        closeModal={() => {
          setmodal();
        }}
        colorScheme="error"
        confirmText="Finalizar"
        loading={!!isWaitingResponse}
      />
      <ModalConfirm
        show={modal?.type === "reopen"}
        title="Reabrir Atendimento"
        text="Tem certeza que deseja reabrir este atendimento?"
        confirm={reopenAttendance}
        closeModal={() => {
          setmodal();
        }}
        colorScheme="primary"
        confirmText="Reabrir"
        loading={!!isWaitingResponse}
      />
      <ModalEndService
        show={modal?.type === "rate"}
        closeModal={() => {
          setmodal();
        }}
        id={attendance_id}
      />
      <ModalPrivateComment
        show={modal?.type === "private_comment"}
        closeModal={() => {
          setmodal();
        }}
        id={attendance_id}
      />
      {me?.type === "Gestor" && (
        <ModalChangeAttendant
          show={modal?.type === "change_attendance"}
          closeModal={() => {
            setmodal();
          }}
          attendance_id={attendance_id}
          user_id={attendance?.user_id}
          category_id={attendance?.category_id}
          resource_id={attendance?.resource_id}
        />
      )}
      <ModalConfirm
        show={modal?.type === "comission_response"}
        title="Enviar Mensagem"
        text={
          <>
            <p className="m-0">Tem certeza que deseja deseja responder em nome da comissão?</p>
            <p className="m-0 small-label">
              Após confirmar e enviar esta mensagem, você será o responsável pelo atendimento.
            </p>
          </>
        }
        confirm={() => {
          submitMessage();
          setmodal();
        }}
        closeModal={() => {
          setmodal();
        }}
        colorScheme="primary"
        loading={!!isWaitingResponse}
      />
    </>
  );
}
